/**
 * Pemformatan.
 *
 * Antarmuka lama punya delapan pemformat ukuran berkas yang berbeda — dua di
 * antaranya di dalam myfiles.php sendiri, dengan jumlah desimal yang tidak
 * sama. Akibatnya berkas yang sama bisa tertulis "1,5 GB" di satu halaman dan
 * "1,53 GB" di halaman lain. Semua pemformatan sekarang berasal dari sini.
 */

const SATUAN = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/** Basis 1024, dua desimal — sama dengan formatBytes() di sisi server. */
export function bytes(nilai: number, presisi = 2): string {
  const v = Math.max(nilai, 0)
  if (v === 0) return '0 B'
  const pow = Math.min(Math.floor(Math.log(v) / Math.log(1024)), SATUAN.length - 1)
  return `${Number((v / 1024 ** pow).toFixed(presisi))} ${SATUAN[pow]}`
}

const BULAN_PENDEK = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
const BULAN_PANJANG = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]

function keDate(nilai: number | string | Date): Date {
  if (nilai instanceof Date) return nilai
  if (typeof nilai === 'number') return new Date(nilai * 1000)
  return new Date(nilai)
}

/** "26 Jul 2026, 14.30" — dipakai di baris daftar pada layar lebar. */
export function tanggalPanjang(nilai: number | string | Date): string {
  const d = keDate(nilai)
  if (Number.isNaN(d.getTime())) return '—'
  const jam = `${String(d.getHours()).padStart(2, '0')}.${String(d.getMinutes()).padStart(2, '0')}`
  return `${d.getDate()} ${BULAN_PENDEK[d.getMonth()]} ${d.getFullYear()}, ${jam}`
}

/** "26 Jul" atau "26 Jul 2025" bila bukan tahun berjalan — untuk meta di ponsel. */
export function tanggalPendek(nilai: number | string | Date): string {
  const d = keDate(nilai)
  if (Number.isNaN(d.getTime())) return '—'
  const tahun = d.getFullYear() === new Date().getFullYear() ? '' : ` ${d.getFullYear()}`
  return `${d.getDate()} ${BULAN_PENDEK[d.getMonth()]}${tahun}`
}

/** "Hari ini, 14.30" · "Kemarin" · "3 hari lalu" · lalu jatuh ke tanggal pendek. */
export function tanggalRelatif(nilai: number | string | Date): string {
  const d = keDate(nilai)
  if (Number.isNaN(d.getTime())) return '—'

  const selisihDetik = Math.floor((Date.now() - d.getTime()) / 1000)
  if (selisihDetik < 60) return 'Baru saja'
  if (selisihDetik < 3600) return `${Math.floor(selisihDetik / 60)} menit lalu`

  const jam = `${String(d.getHours()).padStart(2, '0')}.${String(d.getMinutes()).padStart(2, '0')}`
  const hariIni = new Date()
  const awalHariIni = new Date(hariIni.getFullYear(), hariIni.getMonth(), hariIni.getDate()).getTime()
  const awalTanggal = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const selisihHari = Math.round((awalHariIni - awalTanggal) / 86_400_000)

  if (selisihHari === 0) return `Hari ini, ${jam}`
  if (selisihHari === 1) return `Kemarin, ${jam}`
  if (selisihHari < 7) return `${selisihHari} hari lalu`
  return tanggalPendek(d)
}

/** "12 Agustus 2026" — untuk tanggal kedaluwarsa dan detail. */
export function tanggalLengkap(nilai: number | string | Date): string {
  const d = keDate(nilai)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getDate()} ${BULAN_PANJANG[d.getMonth()]} ${d.getFullYear()}`
}

/**
 * Perkiraan sisa waktu, berbahasa Indonesia.
 * Kata "sebentar" dipakai di bawah 5 detik: menampilkan "3 dtk" pada angka
 * yang berubah tiap saat justru membuat unggahan terasa lebih lama.
 */
export function eta(detik: number): string {
  if (!Number.isFinite(detik) || detik < 0) return '—'
  if (detik < 5) return 'sebentar lagi'
  if (detik < 60) return `${Math.round(detik)} dtk`
  if (detik < 3600) {
    const menit = Math.floor(detik / 60)
    const sisa = Math.round(detik % 60)
    return sisa >= 10 ? `${menit} mnt ${sisa} dtk` : `${menit} menit`
  }
  const jam = Math.floor(detik / 3600)
  const menit = Math.round((detik % 3600) / 60)
  return menit > 0 ? `${jam} jam ${menit} mnt` : `${jam} jam`
}

/** "1.2 MB/dtk" */
export function kecepatan(bytesPerDetik: number): string {
  if (!Number.isFinite(bytesPerDetik) || bytesPerDetik <= 0) return '—'
  return `${bytes(bytesPerDetik, 1)}/dtk`
}

/** "01.23" atau "1.02.03" — untuk durasi media. */
export function durasi(detik: number): string {
  if (!Number.isFinite(detik) || detik < 0) return '—'
  const j = Math.floor(detik / 3600)
  const m = Math.floor((detik % 3600) / 60)
  const d = Math.floor(detik % 60)
  const p = (n: number) => String(n).padStart(2, '0')
  return j > 0 ? `${j}.${p(m)}.${p(d)}` : `${p(m)}.${p(d)}`
}

/** Nama depan saja — dipakai untuk sapaan dan avatar. */
export function namaDepan(nama: string): string {
  return nama.trim().split(/\s+/)[0] ?? nama
}

export function inisial(nama: string): string {
  return (nama.trim()[0] ?? '?').toUpperCase()
}
